/**
 * Sticky Add to Cart Component
 * Provides an accessible floating add to cart button for mobile users
 * that appears when scrolling past the main add to cart button
 */

class StickyAddToCart {
  constructor() {
    this.stickyCart = document.querySelector('[data-sticky-cart]');
    if (!this.stickyCart) return;

    this.stickyButton = this.stickyCart.querySelector('[data-sticky-cart-button]');
    this.productFormId = this.stickyButton?.dataset.productFormId;
    this.mainAddToCartButton = null;
    this.isVisible = false;
    this.scrollThreshold = 0;
    
    this.init();
  }

  init() {
    if (!this.stickyButton || !this.productFormId) return;

    // Move button to body to escape all positioning contexts
    // This must happen early to ensure position: fixed works correctly
    this.moveButtonToBody();

    // Find the main add to cart button
    this.mainAddToCartButton = document.querySelector(`#${this.productFormId}`);
    if (!this.mainAddToCartButton) return;

    // Store reference to main submit button for position checks
    this.mainSubmitButton = document.querySelector(`button[id^="ProductSubmitButton-"]`);

    // Set up intersection observer for scroll detection
    this.setupIntersectionObserver();

    // Also add scroll listener as backup for edge cases
    this.setupScrollListener();

    // Handle button click
    this.stickyButton.addEventListener('click', this.handleAddToCart.bind(this));

    // Listen for variant changes
    this.setupVariantChangeListener();

    // Listen for product form updates
    this.setupProductFormListener();
  }
  
  setupScrollListener() {
    // Backup scroll listener in case IntersectionObserver misses events
    let scrollTimeout;
    this.scrollHandler = () => {
      // Debounce scroll events
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (this.mainSubmitButton) {
          this.checkMainButtonPosition();
        }
      }, 50);
    };
    
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  checkMainButtonPosition() {
    if (!this.mainSubmitButton) return;
    
    const rect = this.mainSubmitButton.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    // Check if button is in viewport
    const isInViewport = rect.top >= 0 && rect.top <= viewportHeight && rect.bottom >= 0;
    
    if (isInViewport) {
      // Button is visible, hide sticky
      this.hideStickyButton();
    } else if (rect.top < -100) {
      // Button is above viewport (scrolled past it)
      this.showStickyButton();
    } else {
      // Button is below viewport (not reached yet)
      this.hideStickyButton();
    }
  }

  moveButtonToBody() {
    // Move the sticky cart to body to escape any positioning contexts
    // This ensures position: fixed works relative to viewport
    if (this.stickyCart && this.stickyCart.parentElement) {
      // Store original parent for accessibility context (if needed for cleanup)
      this.originalParent = this.stickyCart.parentElement;
      
      // Maintain semantic connection with product for screen readers
      // Find the product info container and create ARIA relationship
      const productInfo = document.querySelector('product-info');
      if (productInfo) {
        const productInfoId = productInfo.id || 'product-info';
        if (!productInfo.id) {
          productInfo.id = productInfoId;
        }
        // Link the button to the product context
        this.stickyCart.setAttribute('aria-describedby', productInfoId);
      }
      
      // Append to body - this breaks it out of all positioning contexts
      document.body.appendChild(this.stickyCart);
      
      // Add data attribute to indicate it's been moved
      this.stickyCart.setAttribute('data-moved-to-body', 'true');
    }
  }

  setupIntersectionObserver() {
    // Find the main product submit button to observe
    const mainSubmitButton = document.querySelector(`button[id^="ProductSubmitButton-"]`);
    if (!mainSubmitButton) return;

    const options = {
      root: null,
      rootMargin: '0px 0px 0px 0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.checkButtonPosition(entry);
      });
    }, options);

    this.observer.observe(mainSubmitButton);
    
    // Check initial state on page load after layout settles
    // IntersectionObserver only fires on changes, not initial state
    // Use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.checkInitialPosition(mainSubmitButton);
      }, 100);
    });
  }

  checkInitialPosition(button) {
    // Check the initial position of the button when page loads
    const rect = button.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    // Check if button is in viewport
    const isInViewport = rect.top >= 0 && rect.bottom <= viewportHeight;
    
    if (isInViewport) {
      // Button is visible, hide sticky
      this.hideStickyButton();
    } else if (rect.top < -100) {
      // Button is above viewport (scrolled past it)
      this.showStickyButton();
    } else {
      // Button is below viewport (not reached yet)
      this.hideStickyButton();
    }
  }

  checkButtonPosition(entry) {
    const rect = entry.boundingClientRect;
    
    // Button is in viewport
    if (entry.isIntersecting) {
      this.hideStickyButton();
    } 
    // Button is not in viewport - check if above or below
    else {
      // If button's top is negative, it's above the viewport (scrolled past it)
      // We add a small buffer (100px) so it doesn't show too early
      if (rect.top < -100) {
        this.showStickyButton();
      } else {
        // Button is below viewport or just slightly above - hide sticky button
        this.hideStickyButton();
      }
    }
  }

  showStickyButton() {
    if (this.isVisible) return;
    
    // Don't show if product is out of stock
    const isAvailable = this.stickyCart.getAttribute('data-variant-available') === 'true';
    if (!isAvailable) return;
    
    this.stickyCart.classList.add('is-visible');
    this.stickyCart.setAttribute('aria-hidden', 'false');
    this.isVisible = true;

    // Announce to screen readers (only once)
    if (!this.announced) {
      this.announceToScreenReader('Add to cart button is now available at the bottom right of the screen');
      this.announced = true;
    }
  }

  hideStickyButton() {
    if (!this.isVisible) return;
    
    this.stickyCart.classList.remove('is-visible');
    this.stickyCart.setAttribute('aria-hidden', 'true');
    this.isVisible = false;
  }
  
  completelyHideButton() {
    // Completely hide the button (e.g., when out of stock)
    this.stickyCart.classList.remove('is-visible');
    this.stickyCart.setAttribute('aria-hidden', 'true');
    this.stickyCart.setAttribute('data-variant-available', 'false');
    this.isVisible = false;
  }
  
  enableButton() {
    // Re-enable button when back in stock
    this.stickyCart.setAttribute('data-variant-available', 'true');
  }

  async handleAddToCart(event) {
    event.preventDefault();
    
    if (this.stickyButton.disabled) return;

    // Add loading state
    this.stickyButton.classList.add('is-loading');
    this.stickyButton.disabled = true;

    try {
      // Trigger the main form submission
      const form = document.querySelector(`#${this.productFormId}`);
      if (!form) {
        throw new Error('Product form not found');
      }

      // Get the main submit button and trigger its click event
      const mainSubmitButton = form.querySelector('button[type="submit"][name="add"]');
      if (mainSubmitButton) {
        mainSubmitButton.click();
        
        // Provide haptic feedback on mobile devices
        if ('vibrate' in navigator) {
          navigator.vibrate(50);
        }

        // Announce success to screen readers
        setTimeout(() => {
          this.announceToScreenReader('Item added to cart');
        }, 500);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.announceToScreenReader('Error adding item to cart. Please try again.');
    } finally {
      // Remove loading state after a short delay
      setTimeout(() => {
        this.stickyButton.classList.remove('is-loading');
        this.stickyButton.disabled = false;
      }, 1000);
    }
  }

  setupVariantChangeListener() {
    // Listen for variant change events from the product form
    document.addEventListener('variant:change', (event) => {
      const variant = event.detail?.variant;
      if (variant) {
        this.updateButtonState(variant);
      }
    });

    // Also listen for changes in the product form
    if (this.mainAddToCartButton) {
      const form = this.mainAddToCartButton.closest('form');
      if (form) {
        const variantInput = form.querySelector('.product-variant-id');
        if (variantInput) {
          const observer = new MutationObserver(() => {
            this.syncWithMainButton();
          });
          observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
        }
      }
    }
  }

  setupProductFormListener() {
    // Sync sticky button state with main button state
    const mainSubmitButton = document.querySelector(`button[id^="ProductSubmitButton-"]`);
    if (mainSubmitButton) {
      const observer = new MutationObserver(() => {
        this.syncWithMainButton();
      });

      observer.observe(mainSubmitButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled']
      });

      // Initial sync
      this.syncWithMainButton();
    }
  }

  syncWithMainButton() {
    const mainSubmitButton = document.querySelector(`button[id^="ProductSubmitButton-"]`);
    if (!mainSubmitButton) return;

    const isDisabled = mainSubmitButton.disabled;
    const buttonText = mainSubmitButton.querySelector('span')?.textContent?.trim();
    
    // Check if product is sold out
    const isSoldOut = isDisabled && buttonText && 
                      (buttonText.toLowerCase().includes('sold out') || 
                       buttonText.toLowerCase().includes('unavailable'));

    if (isSoldOut) {
      // Completely hide button when sold out
      this.completelyHideButton();
    } else {
      // Enable button when in stock
      this.enableButton();
      
      // Update button text if available
      if (buttonText) {
        const stickyButtonText = this.stickyButton.querySelector('.sticky-add-to-cart__text');
        if (stickyButtonText) {
          stickyButtonText.textContent = buttonText;
        }
        this.stickyButton.setAttribute('aria-label', buttonText);
      }
    }
  }

  updateButtonState(variant) {
    if (!variant) return;

    const isAvailable = variant.available;
    const buttonText = this.stickyButton.querySelector('.sticky-add-to-cart__text');

    if (isAvailable) {
      this.enableButton();
      if (buttonText) {
        buttonText.textContent = buttonText.dataset.addToCartText || 'Add to cart';
      }
      this.stickyButton.setAttribute('aria-label', 'Add to cart');
    } else {
      // Completely hide when not available
      this.completelyHideButton();
    }
  }

  announceToScreenReader(message) {
    // Create or use existing live region for announcements
    let liveRegion = document.getElementById('sticky-cart-announcer');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'sticky-cart-announcer';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }

    // Clear and set new message
    liveRegion.textContent = '';
    setTimeout(() => {
      liveRegion.textContent = message;
    }, 100);
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }

    if (this.stickyButton) {
      this.stickyButton.removeEventListener('click', this.handleAddToCart);
    }

    // Move button back to original parent if it was moved
    if (this.stickyCart && this.originalParent && this.stickyCart.getAttribute('data-moved-to-body') === 'true') {
      try {
        this.originalParent.appendChild(this.stickyCart);
        this.stickyCart.removeAttribute('data-moved-to-body');
      } catch (e) {
        // Original parent may no longer exist, just remove from body
        if (this.stickyCart.parentElement) {
          this.stickyCart.parentElement.removeChild(this.stickyCart);
        }
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyAddToCart();
  });
} else {
  new StickyAddToCart();
}

// Re-initialize on section reload (Shopify theme editor)
document.addEventListener('shopify:section:load', () => {
  new StickyAddToCart();
});

